/**
 * Derives level geometry from a profile level: rooms in, boxes out.
 *
 * Walls are computed, not authored — and they are **per room, two-sided**: each room
 * lines the inside of its own boundary with a thin wall, so a boundary shared by two
 * rooms carries one wall face per room. That is what lets every room have its own
 * wall color and pattern (like wallpaper actually works), at the cost of a handful of
 * extra boxes. Door openings are cut from both sides and a lintel fills the space
 * above them. The output is plain boxes — the renderer and the physics world are
 * built from it, and a future maze generator only has to emit a `LevelSpec` to get
 * the same treatment.
 */

import type { OutOfBounds } from '../bounds';
import type { ArriveAt } from '../progression';
import { OBJECT_KINDS, PLACEMENT_CLEARANCE } from '../objectKinds';
import { buildGrounds, groundsRect } from '../maze/buildGrounds';
import { generateMaze } from '../maze/generateMaze';
import { placeTableItems } from '../maze/placeTableItems';
import type { Ladder } from '../maze/placeLadders';
import type { Table } from '../maze/placeTables';
import { buildWalls, WALL_THICKNESS } from './buildWalls';
import { DEFAULT_CEILING_HEIGHT, DEFAULT_TRIM_COLOR } from './validate';
import {
  sharedBoundary,
  type Box,
  type DepositObjectsObjective,
  type FloorPattern,
  type GroundPoint,
  type LevelSpec,
  type ObjectKind,
  type Palette,
  type PathSegment,
  type Rect,
  type RoomSpec,
  type Spawn,
  type Vec3,
  type WallPattern,
} from './types';

// Re-exported so `buildGeometry` stays the one import site for level geometry.
export { WALL_THICKNESS };

export const FLOOR_THICKNESS = 0.5;
export const CEILING_THICKNESS = 0.2;
/**
 * Cardboard boxes when the profile does not give a size — kept as a named export because
 * that is what the house levels were laid out against. Every other kind's default comes
 * from `OBJECT_KINDS`, which is where a kind's size now lives.
 */
export const DEFAULT_OBJECT_SIZE: Vec3 = OBJECT_KINDS['cardboard-box'].size;
/** How tall the invisible drop-zone sensor is — enough to catch a box at rest. */
export const ZONE_HEIGHT = 1;
/**
 * The room id an outdoor level's grass is filed under. It is not a room and never
 * appears in `rooms`, but a floor has to belong to something, and giving it a name means
 * the footprint, the collider set and the exterior fit need no special case.
 */
export const GROUNDS_ROOM_ID = 'grounds';

export type FloorSurface = { roomId: string; box: Box; color: string; pattern: FloorPattern };
export type CeilingSurface = { roomId: string; box: Box; color: string };

export type RoomWall = {
  roomId: string;
  box: Box;
  color: string;
  pattern: WallPattern;
  /** False for lintels and other partial-height pieces — they skip baseboard trim. */
  isFullHeight: boolean;
  /**
   * Unit vector from the wall towards the inside of its room, on the ground plane.
   * Trim has to stand proud on the side the player can see, and a box alone cannot say
   * which side that is.
   */
  inward: GroundPoint;
};

export type Doorway = {
  id: string;
  /** The axis the doorway's boundary line is perpendicular to — see `SharedBoundary`. */
  axis: 'x' | 'z';
  /** Centre of the opening; y is half the door height. */
  center: Vec3;
  width: number;
  height: number;
  isInitiallyOpen: boolean;
};

/**
 * One room's side of a doorway. A `Doorway` sits on the boundary between two rooms and
 * so belongs to neither wall face; a casing has to be drawn on both, and each room's
 * wall is inset half a thickness towards its own middle. This is that per-room view.
 */
export type DoorFace = {
  doorId: string;
  roomId: string;
  /** Centre of the opening on this room's wall face; y is half the door height. */
  center: Vec3;
  width: number;
  height: number;
  /** Towards the inside of this face's room — see `RoomWall.inward`. */
  inward: GroundPoint;
};

/**
 * A window opening, as the room that owns it sees it. Unlike a doorway there is only
 * ever one of these per window: a window is in an exterior wall, so no second room sees
 * its other side.
 */
export type WindowFace = {
  windowId: string;
  roomId: string;
  /** Centre of the opening on the room's wall face; y is midway between sill and head. */
  center: Vec3;
  width: number;
  sill: number;
  head: number;
  inward: GroundPoint;
};

export type DropZone = { objectiveId: string; box: Box };

export type PlacedObject = { id: string; kind: ObjectKind; box: Box };

/**
 * An outdoor level's maze grounds, as plain boxes. Split by what each set is *for*
 * rather than by what it looks like: hedges are seen and collided with, water is only
 * seen, barriers are only collided with, and the exit zone is neither — it is a sensor.
 * Conflating them is how a moat becomes swimmable or a bank becomes a visible wall.
 */
export type BuiltGrounds = {
  /** Collide, and drawn as one instanced set. */
  hedges: Box[];
  /**
   * The lawn the player may not stand on, derived from the maze. A rule, not geometry:
   * nothing here is drawn or collided with, and `collidableBoxes` never reads it.
   */
  bounds: OutOfBounds;
  /** The grass the level stands on — what a recovered object's pose must be over. */
  lawn: Rect;
  /** Drawn, never collidable — the moat is meant to look crossable and not be. */
  waterPanels: Box[];
  /** Collide, never drawn: the invisible line that stops the player at the bank. */
  barriers: Box[];
  /**
   * Ladders against the hedge faces. Drawn and climbable, never collidable: a solid
   * ladder blocks the corridor it stands in and gets climbed by the character
   * controller's autostep, rung by rung, whether or not that was asked for.
   */
  ladders: Ladder[];
  /**
   * Tables in the maze's dead ends. Drawn, and solid — but they carry their own bodies
   * rather than joining `collidableBoxes`, because a table can be dragged and the static
   * level collider never moves.
   */
  tables: Table[];
  /**
   * The portal gates: one filling the gap in the outer hedge at each end of the maze.
   * Drawn and sensed, never collidable — see `collidableBoxes`. Both are always built;
   * whether the entry one is mounted depends on whether another level leads in here,
   * which is a question about the profile rather than about the maze.
   */
  exitGate: Box;
  entryGate: Box;
  /**
   * Where a player who walked through one of the gates is put down — inside the maze,
   * past that gate, facing in. `exitArrival` is for a player returning through the next
   * level's entry gate; `entryArrival` for one crossing forward into this level.
   */
  exitArrival: Spawn;
  entryArrival: Spawn;
  /** The reach-exit objective the exit gate completes, if the level has one. */
  exitObjectiveId: string | null;
  hedgeHeight: number;
  /**
   * The walkable corridor graph, as straight legs between the centres of open
   * neighbouring cells, plus one stub out of each mouth to the grass edge. Purely
   * decorative — the mud path is painted along these — but derived here because only the
   * maze data knows which cells connect.
   */
  paths: PathSegment[];
};

export type BuiltLevel = {
  spawn: Spawn;
  floors: FloorSurface[];
  ceilings: CeilingSurface[];
  walls: RoomWall[];
  steps: readonly Box[];
  doorways: Doorway[];
  /** Two per doorway, one for each room it connects. */
  doorFaces: DoorFace[];
  /** One per window. */
  windowFaces: WindowFace[];
  /** The room specs this level was built from — fixtures are placed against them. */
  rooms: readonly RoomSpec[];
  zones: DropZone[];
  objects: PlacedObject[];
  /** Fallback wall color for pieces that belong to no single room (steps). */
  wallColor: string;
  /** One colour for all skirting, cornice and casings in the house. */
  trimColor: string;
  /** The maze this level stands on, or null for a level of rooms. */
  grounds: BuiltGrounds | null;
};

/**
 * Where the player stands when a level appears under them.
 *
 * Three cases and one rule: a player who walked through a gate comes out on the far side
 * of it, inside the maze, facing in — so the maze is ahead and the way back is behind. A
 * run that is only starting has no gate behind it, and begins at the spawn the profile
 * authored. `hasEntryGate` is the profile's question, not the geometry's: both gates are
 * always built, but the entry one only stands where some other level leads here, and
 * without it an `entry` arrival is the start of a run rather than a crossing.
 */
export function arrivalSpawn(built: BuiltLevel, arriveAt: ArriveAt, hasEntryGate: boolean): Spawn {
  const { grounds } = built;
  if (!grounds) return built.spawn;
  if (arriveAt === 'exit') return grounds.exitArrival;
  return hasEntryGate ? grounds.entryArrival : built.spawn;
}










function slabOver(rect: Rect, centerY: number, thickness: number): Box {
  return {
    center: { x: rect.x + rect.width / 2, y: centerY, z: rect.z + rect.depth / 2 },
    size: { x: rect.width, y: thickness, z: rect.depth },
  };
}

function roomBox(room: RoomSpec, centerY: number, thickness: number): Box {
  return slabOver(room.rect, centerY, thickness);
}

/** A pane is thin enough to be invisible edge-on and thick enough not to be tunnelled. */
export const GLASS_THICKNESS = 0.05;

/**
 * The glass filling each window opening.
 *
 * A window is a hole cut clean through an exterior wall, so without this a thrown box
 * sails out of the house and an objective can be lost somewhere the player can never
 * reach. Glass is what makes a window a window rather than a doorway: you can see through
 * it and light comes in, but nothing solid passes.
 */
export function windowGlassBoxes(built: BuiltLevel): Box[] {
  return built.windowFaces.map((face) => {
    const acrossX = face.inward.x !== 0;
    return {
      center: { x: face.center.x, y: face.center.y, z: face.center.z },
      size: {
        x: acrossX ? GLASS_THICKNESS : face.width,
        y: face.head - face.sill,
        z: acrossX ? face.width : GLASS_THICKNESS,
      },
    };
  });
}

/**
 * Everything the player can collide with. Trim is pointedly absent: mouldings, casings
 * and window frames are decoration, and keeping them out of this list is what
 * guarantees they cannot narrow a doorway or catch a player walking along a wall. Glass
 * is not decoration — it is the only thing closing the hole.
 */
export function collidableBoxes(built: BuiltLevel): Box[] {
  return [
    ...built.floors.map((floor) => floor.box),
    ...built.ceilings.map((ceiling) => ceiling.box),
    ...built.walls.map((wall) => wall.box),
    ...built.steps,
    ...windowGlassBoxes(built),
    // Outdoors, hedges are the walls and the bank barriers are the outer shell. The
    // water and the exit sensor are pointedly not here: one is scenery, the other a
    // trigger, and a collider on either would break the level.
    ...(built.grounds?.hedges ?? []),
    ...(built.grounds?.barriers ?? []),
  ];
}

/**
 * How much ground the house covers, for anything that has to be fitted around it — the
 * sun's shadow frustum and the exterior world both are.
 *
 * Two radii, because the callers want different things and conflating them is a real bug
 * rather than a nicety. `halfSpan` is half the longer side — enough for the sun's
 * axis-aligned frustum indoors, where the corners of the plan sit behind walls anyway.
 * `radius` is the circle that actually encloses the footprint, corners included: anything
 * placed *around* the house has to clear its diagonal, and so does a shadow camera over
 * open ground, where a sun off one corner casts along that same diagonal. A square
 * footprint's corner is 41% further out than its wall.
 */
export type Footprint = {
  center: GroundPoint;
  /** Half the longer side. */
  halfSpan: number;
  /** Half the diagonal: the radius of a circle containing the whole footprint. */
  radius: number;
};

export function levelFootprint(built: BuiltLevel): Footprint {
  if (built.floors.length === 0) {
    return { center: { x: 0, z: 0 }, halfSpan: 0, radius: 0 };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const { box } of built.floors) {
    minX = Math.min(minX, box.center.x - box.size.x / 2);
    maxX = Math.max(maxX, box.center.x + box.size.x / 2);
    minZ = Math.min(minZ, box.center.z - box.size.z / 2);
    maxZ = Math.max(maxZ, box.center.z + box.size.z / 2);
  }

  return {
    center: { x: (minX + maxX) / 2, z: (minZ + maxZ) / 2 },
    halfSpan: Math.max(maxX - minX, maxZ - minZ) / 2,
    radius: Math.hypot(maxX - minX, maxZ - minZ) / 2,
  };
}

export function buildGeometry(level: LevelSpec, palette: Palette): BuiltLevel {
  const ceilingHeight = level.ceilingHeight ?? DEFAULT_CEILING_HEIGHT;
  const roomsById = new Map(level.rooms.map((room) => [room.id, room]));

  const colorOf = (room: RoomSpec, key: 'floor' | 'walls' | 'ceiling') =>
    room.palette?.[key] ?? palette.room[key];

  const { walls, doorFaces, windowFaces } = buildWalls(
    level.rooms,
    level.doors,
    ceilingHeight,
    (room) => ({
      color: colorOf(room, 'walls'),
      pattern: room.palette?.wallPattern ?? palette.room.wallPattern ?? 'wallpaper',
    }),
  );

  // A level declares grounds or rooms, never both (validate.ts), so nothing here is a
  // branch on level *kind*: the rooms below simply come out empty for a maze, and the
  // grass is one more floor.
  const groundsSpec = level.grounds;
  const grounds = groundsSpec
    ? buildGrounds(
        generateMaze(groundsSpec.seed, groundsSpec.columns, groundsSpec.rows),
        groundsSpec,
        level.objectives.find((objective) => objective.kind === 'reach-exit')?.id ?? null,
      )
    : null;
  const grassFloors: FloorSurface[] = groundsSpec
    ? [
        {
          roomId: GROUNDS_ROOM_ID,
          box: slabOver(
            groundsRect(groundsSpec.columns, groundsSpec.rows),
            -FLOOR_THICKNESS / 2,
            FLOOR_THICKNESS,
          ),
          color: palette.room.floor,
          pattern: 'hedge',
        },
      ]
    : [];

  return {
    spawn: {
      position: { x: level.spawn.position.x, y: 0, z: level.spawn.position.z },
      yaw: level.spawn.yaw,
    },
    floors: [
      ...level.rooms.map((room): FloorSurface => {
        const pattern: FloorPattern =
          room.palette?.floorPattern ?? palette.room.floorPattern ?? 'wood';
        return {
          roomId: room.id,
          box: roomBox(room, -FLOOR_THICKNESS / 2, FLOOR_THICKNESS),
          color: colorOf(room, 'floor'),
          pattern,
        };
      }),
      ...grassFloors,
    ],
    ceilings: level.rooms.map((room) => ({
      roomId: room.id,
      box: roomBox(room, ceilingHeight + CEILING_THICKNESS / 2, CEILING_THICKNESS),
      color: colorOf(room, 'ceiling'),
    })),
    walls,
    doorFaces,
    windowFaces,
    rooms: level.rooms,
    steps: level.steps ?? [],
    doorways: level.doors.map((door) => {
      const [roomA, roomB] = door.betweenRooms;
      const boundary = sharedBoundary(roomsById.get(roomA)!.rect, roomsById.get(roomB)!.rect);
      return {
        id: door.id,
        axis: boundary?.axis ?? 'x',
        center: { x: door.center.x, y: door.height / 2, z: door.center.z },
        width: door.width,
        height: door.height,
        isInitiallyOpen: door.isInitiallyOpen ?? false,
      };
    }),
    // Only a deposit objective has a zone to drop into; a reach-exit objective's zone is
    // the maze's own exit, and lives on `grounds`.
    zones: level.objectives
      .filter(
        (objective): objective is DepositObjectsObjective => objective.kind === 'deposit-objects',
      )
      .map((objective) => ({
        objectiveId: objective.id,
        box: {
          center: {
            x: objective.zone.rect.x + objective.zone.rect.width / 2,
            y: ZONE_HEIGHT / 2,
            z: objective.zone.rect.z + objective.zone.rect.depth / 2,
          },
          size: { x: objective.zone.rect.width, y: ZONE_HEIGHT, z: objective.zone.rect.depth },
        },
      })),
    objects: [
      // The maze's own items come first only so the list has a stable order; nothing
      // downstream cares which end of it a carryable came from.
      ...(grounds && groundsSpec
        ? placeTableItems(grounds.tables, groundsSpec.seed, groundsSpec.tableItems ?? [])
        : []),
      ...level.objects.map((object) => {
        const size = object.size ?? OBJECT_KINDS[object.kind].size;
        return {
          id: object.id,
          kind: object.kind,
          box: {
            center: {
              x: object.position.x,
              y: size.y / 2 + PLACEMENT_CLEARANCE,
              z: object.position.z,
            },
            size,
          },
        };
      }),
    ],
    wallColor: palette.room.walls,
    trimColor: palette.trim ?? DEFAULT_TRIM_COLOR,
    grounds,
  };
}
